import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["loadingSpinner"];
  static values = {
    autoplay: Boolean,
    muted: Boolean,
  };

  connect() {
    this.video = this.element.querySelector("video") || this.element;
    this.abortController = new AbortController();
    this.destroyed = false;

    this.detectMobileDevice();
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.initializeLoadingUI();
    this.hideLoading();
    this.initializeHls();
  }

  detectMobileDevice() {
    const ua = navigator.userAgent;
    this.isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    this.isAndroid = /Android/.test(ua);
    this.isMobile = this.isIOS || this.isAndroid;
  }

  async initializeHls() {
    const hlsUrl = this.element.dataset.hlsUrl;
    if (!hlsUrl) return;

    // Safari supports HLS natively — store MP4 source for fallback
    if (this.video.canPlayType("application/vnd.apple.mpegurl")) {
      this.nativeHlsUrl = hlsUrl;
      this.video.src = hlsUrl;
      return;
    }

    const { default: Hls } = await import("hls.js/dist/hls.light.min.js");

    if (this.destroyed) return;
    if (!Hls.isSupported()) return;

    this.hls = new Hls({
      maxBufferLength: 30,
      maxBufferSize: 60 * 1000 * 1000,
      backBufferLength: 60,
      capLevelToPlayerSize: true,
      capLevelOnFPSDrop: true,
      startLevel: -1,
      abrEwmaDefaultEstimate: 500_000,
      fragLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 10_000,
          maxLoadTimeMs: 120_000,
          timeoutRetry: {
            maxNumRetry: 2,
            retryDelayMs: 0,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 6,
            retryDelayMs: 1000,
            maxRetryDelayMs: 8000,
            backoff: "exponential",
          },
        },
      },
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 10_000,
          maxLoadTimeMs: 120_000,
          timeoutRetry: {
            maxNumRetry: 2,
            retryDelayMs: 0,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 3,
            retryDelayMs: 1000,
            maxRetryDelayMs: 8000,
            backoff: "exponential",
          },
        },
      },
    });

    this.hls.attachMedia(this.video);
    this.hls.loadSource(hlsUrl);
    this.hlsRecoverAttempts = 0;
    this.hlsNetworkRetries = 0;

    // Handle errors with recovery
    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      if (
        data.type === Hls.ErrorTypes.NETWORK_ERROR &&
        this.hlsNetworkRetries < 2
      ) {
        this.hlsNetworkRetries++;
        this.hls.startLoad();
      } else if (
        data.type === Hls.ErrorTypes.MEDIA_ERROR &&
        this.hlsRecoverAttempts < 2
      ) {
        this.hlsRecoverAttempts++;
        this.hls.recoverMediaError();
      } else {
        // Fatal non-recoverable — fall back to MP4 source
        this.destroyHls();
        this.video.removeAttribute("src");
        this.video.load();
      }
    });
  }

  destroyHls() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  setupEventListeners() {
    const signal = this.abortController.signal;

    this.video.addEventListener("loadstart", () => this.onLoadStart(), {
      signal,
    });
    this.video.addEventListener("loadeddata", () => this.hideLoading(), {
      signal,
    });
    this.video.addEventListener("canplay", () => this.hideLoading(), {
      signal,
    });
    this.video.addEventListener("canplaythrough", () => this.hideLoading(), {
      signal,
    });
    this.video.addEventListener("waiting", () => this.onWaiting(), { signal });
    this.video.addEventListener("playing", () => this.onPlaying(), { signal });
    this.video.addEventListener("stalled", () => this.showLoading(), {
      signal,
    });
    this.video.addEventListener("seeking", () => this.showLoading(), {
      signal,
    });
    this.video.addEventListener("seeked", () => this.hideLoading(), {
      signal,
    });
    this.video.addEventListener("error", (e) => this.onError(e), { signal });

    if (this.isAndroid) {
      this.video.addEventListener(
        "suspend",
        () => {
          if (this.video.readyState < 3 && !this.video.paused) {
            this.showLoading();
          }
        },
        { signal },
      );
    }
  }

  disconnect() {
    this.destroyed = true;
    this.destroyHls();

    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    clearTimeout(this.bufferingTimeout);
    clearTimeout(this.hideLoadingTimeout);
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.hls) {
              this.hls.startLoad();
            } else if (this.video.preload === "none") {
              this.video.preload = "metadata";
              if (this.isAndroid && navigator.userAgent.includes("Chrome")) {
                this.video.load();
              }
            }
          } else {
            if (this.hls) {
              this.hls.stopLoad();
            }
            if (!this.video.paused && !this.userInteracted) {
              this.video.pause();
            }
          }
        });
      },
      { threshold: 0.1 },
    );

    this.observer.observe(this.video);
  }

  initializeLoadingUI() {
    this.userInteracted = false;
    const signal = this.abortController.signal;

    const markInteraction = () => {
      this.userInteracted = true;
    };
    this.video.addEventListener("play", markInteraction, { once: true, signal });
    this.video.addEventListener("click", markInteraction, { once: true, signal });
    this.video.addEventListener("touchstart", markInteraction, { once: true, signal });

    if (!this.hasLoadingSpinnerTarget) {
      this.createLoadingSpinner();
    }

    setTimeout(() => this.hideLoading(), 0);
  }

  createLoadingSpinner() {
    const spinner = document.createElement("div");
    spinner.className =
      "absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none";
    spinner.dataset.videoPlayerTarget = "loadingSpinner";

    const indicator = document.createElement("div");
    indicator.className = "loading loading-spinner loading-lg text-white";
    spinner.appendChild(indicator);

    this.element.appendChild(spinner);
  }

  // Event handlers
  onLoadStart() {
    if (this.userInteracted && this.video.readyState < 3) {
      this.showLoading();
    }
  }

  onWaiting() {
    this.bufferingTimeout = setTimeout(() => this.showLoading(), 500);
  }

  onPlaying() {
    clearTimeout(this.bufferingTimeout);
    this.hideLoading();
  }

  onError(_event) {
    clearTimeout(this.bufferingTimeout);

    // If native HLS failed (Safari), try MP4 fallback before showing error
    if (this.nativeHlsUrl && !this.nativeHlsFallbackAttempted) {
      this.nativeHlsFallbackAttempted = true;
      this.video.removeAttribute("src");

      // Listen for fallback outcome — <source> errors don't bubble, so use capture
      const onFallbackError = () => {
        this.video.removeEventListener("canplay", onFallbackSuccess);
        this.showFatalError();
      };
      const onFallbackSuccess = () => {
        this.video.removeEventListener("error", onFallbackError, true);
      };
      this.video.addEventListener("canplay", onFallbackSuccess, { once: true });
      this.video.addEventListener("error", onFallbackError, { capture: true, once: true });

      this.video.load();
      return;
    }

    this.showFatalError();
  }

  showFatalError() {
    this.hideLoading();

    if (this.hasLoadingSpinnerTarget) {
      const target = this.loadingSpinnerTarget;
      target.replaceChildren();

      const container = document.createElement("div");
      container.className = "text-white text-center";

      const svg = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      svg.setAttribute("class", "w-16 h-16 mx-auto mb-4");
      svg.setAttribute("fill", "none");
      svg.setAttribute("stroke", "currentColor");
      svg.setAttribute("viewBox", "0 0 24 24");
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      path.setAttribute("stroke-width", "2");
      path.setAttribute(
        "d",
        "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
      );
      svg.appendChild(path);
      container.appendChild(svg);

      const message = document.createElement("p");
      message.className = "text-lg";
      message.textContent = "Video kon niet worden geladen";
      container.appendChild(message);

      const button = document.createElement("button");
      button.className = "btn btn-sm btn-outline btn-white mt-4";
      button.textContent = "Probeer opnieuw";
      button.addEventListener("click", () => location.reload());
      container.appendChild(button);

      target.appendChild(container);
      target.classList.add("opacity-100");
    }
  }

  showLoading() {
    if (this.hasLoadingSpinnerTarget && this.userInteracted) {
      this.loadingSpinnerTarget.style.display = "";
      this.loadingSpinnerTarget.classList.remove("opacity-0");
      void this.loadingSpinnerTarget.offsetHeight;
      this.loadingSpinnerTarget.classList.add("opacity-100");
    }
  }

  hideLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("opacity-100");
      void this.loadingSpinnerTarget.offsetHeight;
      this.loadingSpinnerTarget.classList.add("opacity-0");

      if (this.isMobile) {
        clearTimeout(this.hideLoadingTimeout);
        this.hideLoadingTimeout = setTimeout(() => {
          if (
            this.hasLoadingSpinnerTarget &&
            this.loadingSpinnerTarget.classList.contains("opacity-0")
          ) {
            this.loadingSpinnerTarget.style.display = "none";
          }
        }, 300);
      }
    }
  }
}
